import { useState, useEffect } from "react";
import { PlannedPost } from "@/lib/api/interfaces/posts.interface";
import { CalendarClock, AlertCircle, LayoutTemplate, Calendar, UserCircle } from "lucide-react";
import { StandardGradientHeader } from "@/util-components/dialog/components/StandardGradientHeader";
import { Badge } from "@/ui-components/ui/badge";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { getSocialIcon, getSocialColorClasses } from "@/page-components/post-generator/utils/socialUtils";
import { cn } from "@/lib/utils/cn";
import { usePostsDeletePlanned, usePostsReschedulePlanned, usePostsGetPlannedPost } from "@/lib/api/requests/posts.requests";
import { toast } from "@/lib/toast";
import { Input } from "@/ui-components/ui/input";
import { DualImageDisplay } from "@/util-components/dialog/components/DualImageDisplay";
import { MentionedEntitiesList } from "./MentionedEntitiesList";
import { DetailsBaseDialog } from "@/util-components/dialog/base-dialogs/details-base-dialog";
import { DialogTextSection, DialogContentSection, DialogContentItem } from "@/util-components/dialog/base-dialogs/dialog-sections";

interface PlannedPostDialogProps {
  open: boolean;
  onClose: () => void;
  post: PlannedPost | null;
}

export const PlannedPostDialog = ({ open, onClose, post }: PlannedPostDialogProps) => {
  const [newDate, setNewDate] = useState("");
  
  const { mutate: deletePost, isPending: isDeleting } = usePostsDeletePlanned();
  const { mutate: reschedulePost, isPending: isReschedulingMutation } = usePostsReschedulePlanned();

  const { data: detailedPost, isLoading: isFetchingDetails } = usePostsGetPlannedPost(
    open && post ? post._id : undefined,
    { enabled: open && !!post }
  );

  const displayPost = detailedPost || post;

  useEffect(() => {
    if (displayPost?.scheduledFor) {
      setNewDate(formatDateForInput(displayPost.scheduledFor));
    }
  }, [displayPost?.scheduledFor]);

  if (!post || !displayPost) return null;

  const isFailed = displayPost.status === "FAILED";

  const handleDelete = async () => {
    deletePost(displayPost._id, {
      onSuccess: () => {
        toast.success("Geplanter Post wurde gelöscht");
        onClose();
      },
      onError: (error) => {
        toast.error("Geplanter Post konnte nicht gelöscht werden", { error });
      }
    });
  };

  const handleReschedule = (onSaveSuccess: () => void) => {
    if (!newDate) {
      toast.error("Bitte Datum und Uhrzeit auswählen");
      return;
    }
    const isoDate = new Date(newDate).toISOString();
    reschedulePost({ id: displayPost._id, scheduledFor: isoDate }, {
      onSuccess: () => {
        toast.success("Post wurde erfolgreich verschoben");
        onSaveSuccess();
      },
      onError: (error) => {
        toast.error("Post konnte nicht verschoben werden", { error });
      }
    });
  };

  const formatDateString = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd. MMMM yyyy, HH:mm 'Uhr'", { locale: de });
    } catch {
      return dateString;
    }
  };

  const formatDateForInput = (dateString: string) => {
    try {
      return format(new Date(dateString), "yyyy-MM-dd'T'HH:mm");
    } catch {
      return "";
    }
  };

  const headerContent = (
    <StandardGradientHeader>
      <div className="flex flex-col items-center justify-center gap-2 w-full">
        <div className="w-full flex justify-center">
          <DualImageDisplay 
            templateUrl={displayPost.template?.thumbnailUrl} 
            postUrl={displayPost.thumbnailUrl || displayPost.thumbnailPath} 
            templateId={displayPost.templateId}
          />
        </div>
        {isFailed ? (
          <Badge variant="destructive" className="flex items-center gap-1.5 shadow-sm mt-1">
            <AlertCircle className="w-3 h-3" />
            Fehlgeschlagen
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-white/20 text-white border-white/30 flex items-center gap-1.5 shadow-sm mt-1">
            <CalendarClock className="w-3 h-3" />
            Geplant für {formatDateString(displayPost.scheduledFor)}
          </Badge>
        )}
      </div>
    </StandardGradientHeader>
  );

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onClose}
      icon={CalendarClock}
      title="Geplanter Post"
      description="Details zu deinem geplanten Post."
      
      deleteTitle="Geplanten Post löschen"
      deleteItemTitle={displayPost.message?.substring(0, 30) || "Diesen Post"}
      onDeleteConfirm={handleDelete}
      isDeleting={isDeleting}
      
      editButtonLabel="Datum ändern"
      editButtonIcon={CalendarClock}
      saveButtonLabel="Speichern"
      isSaving={isReschedulingMutation}
      canSave={!!newDate}
      formId="reschedule-post-form"
      
      maxWidth="xl"
      
      detailsContent={
        <div className="space-y-6">
          {headerContent}
          
          {isFailed && displayPost.error && (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-5 flex items-start gap-4 mb-4">
              <div className="p-3 bg-red-100 rounded-xl shrink-0">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex flex-col pt-0.5">
                <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest mb-0.5">Fehler</span>
                <p className="font-bold text-red-900 text-sm leading-tight">
                  {typeof displayPost.error === 'string' 
                    ? displayPost.error 
                    : (displayPost.error.message || "Ein unbekannter Fehler ist aufgetreten.")}
                </p>
              </div>
            </div>
          )}

          <DialogContentSection title="Informationen" icon={CalendarClock}>
            <div className="grid gap-3 sm:grid-cols-2">
              <DialogContentItem 
                icon={Calendar} 
                label="Zeitpunkt" 
                value={<p className="text-sm leading-tight">{formatDateString(displayPost.scheduledFor)}</p>} 
              />

              <DialogContentItem 
                icon={LayoutTemplate} 
                label="Plattformen" 
                value={
                  <div className="flex flex-wrap items-center gap-2 mt-0.5">
                    {(displayPost.options?.instagramPost || displayPost.options?.instagramStory) && (
                      <div className={cn("flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold", getSocialColorClasses("INSTAGRAM"))}>
                        {getSocialIcon('INSTAGRAM', 'w-3 h-3')}
                        {displayPost.options?.instagramStory ? 'Story' : 'Post'}
                      </div>
                    )}
                    {(displayPost.options?.facebookPost || displayPost.options?.facebookStory) && (
                      <div className={cn("flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold", getSocialColorClasses("FACEBOOK"))}>
                        {getSocialIcon('FACEBOOK', 'w-3 h-3')}
                        {displayPost.options?.facebookStory ? 'Story' : 'Post'}
                      </div>
                    )}
                    {(displayPost.options?.whatsappPost || (displayPost.whatsappGroupIds && displayPost.whatsappGroupIds.length > 0)) && (
                      <div className={cn("flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold", getSocialColorClasses("WHATSAPP"))}>
                        {getSocialIcon('WHATSAPP', 'w-3 h-3')}
                        Post
                      </div>
                    )}
                  </div>
                } 
              />

              <DialogContentItem 
                icon={UserCircle} 
                label="Geplant von" 
                value={
                  isFetchingDetails ? (
                    <div className="h-5 w-24 bg-gray-200 animate-pulse rounded mt-0.5"></div>
                  ) : displayPost.user ? (
                    <p className="text-sm leading-tight">
                      {displayPost.user.firstName} {displayPost.user.lastName}
                    </p>
                  ) : (
                    <span className="text-sm font-medium text-gray-500">Unbekannt</span>
                  )
                } 
              />
            </div>
          </DialogContentSection>

          <MentionedEntitiesList 
            entities={displayPost.mentionedEntities || []} 
            isLoading={isFetchingDetails} 
          />

          {displayPost.message && (
            <div className="pt-2">
              <DialogTextSection 
                title="Bildunterschriften" 
                icon={LayoutTemplate} 
                text={displayPost.message} 
              />
            </div>
          )}
        </div>
      }
      
      editContent={(onSaveSuccess) => (
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-5 sm:space-y-6">
          {headerContent}
          <form 
            id="reschedule-post-form" 
            onSubmit={(e) => {
              e.preventDefault();
              handleReschedule(onSaveSuccess);
            }} 
            className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4 shadow-sm"
          >
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Neues Datum & Uhrzeit</label>
              <Input 
                type="datetime-local" 
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="h-12 text-base"
              />
            </div>
          </form>
        </div>
      )}
    />
  );
};
